import axios from 'axios';
import type { ApiResponse } from '@shared/types/api.types';
import { store, persistor } from '../../store/store';
import { setCredentials, logout } from '../../store/slices/authSlice';
import {
  notifyColdStartRetry,
  notifyColdStartResolved,
  notifyColdStartFailed,
} from './serverWakeUp';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';

/**
 * 인증 정보 완전 클리어 및 로그인 페이지로 리다이렉트
 * - Redux store 초기화
 * - localStorage 클리어
 * - redux-persist 저장소 클리어
 */
const clearAuthAndRedirect = async () => {
  // Redux store 초기화
  store.dispatch(logout());

  // redux-persist 저장소 완전 클리어
  await persistor.purge();

  // localStorage 완전 클리어 (혹시 남아있을 수 있는 데이터)
  localStorage.clear();

  // 현재 경로에 따라 적절한 로그인 페이지로 리다이렉트
  const currentPath = window.location.pathname;
  let loginPath = '/login';
  if (currentPath.startsWith('/admin')) {
    loginPath = '/admin/login';
  } else if (currentPath.startsWith('/trainer')) {
    loginPath = '/trainer/login';
  }
  window.location.href = loginPath;
};

// Axios 인스턴스 생성
export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor - 토큰 자동 첨부
apiClient.interceptors.request.use(
  (config) => {
    // Redux Store에서 토큰 읽기 (메모리)
    try {
      const state = store.getState();
      let accessToken = state.auth.tokens?.accessToken;

      // Redux Rehydration 지연 대비: store에 없으면 로컬스토리지 직접 조회
      if (!accessToken) {
        const persistRootRaw = localStorage.getItem('persist:hellgater-root');
        if (persistRootRaw) {
          try {
            const persistRoot = JSON.parse(persistRootRaw);
            const authState = JSON.parse(persistRoot.auth);
            accessToken = authState?.tokens?.accessToken;
          } catch (e) {
            // 무시
          }
        }
      }

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    } catch (error) {
      console.error('Failed to get access token from store:', error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Cold start 에러 판별
 * Fly.io auto-stop 후 서버가 깨어나는 동안 발생하는 에러
 */
const isColdStartError = (error: any): boolean => {
  if (!error.config) return false;
  // 네트워크 에러 (서버 미응답)
  if (error.code === 'ERR_NETWORK') return true;
  // 타임아웃 에러 (서버가 켜지는 데 15초 이상 소요됨)
  if (error.code === 'ECONNABORTED' && error.message?.includes('timeout')) return true;
  // Fly.io 프록시 에러 (백엔드 미시작)
  const status = error.response?.status;
  if (status === 502 || status === 503) return true;
  return false;
};

const MAX_COLD_START_RETRIES = 3;
const COLD_START_DELAYS = [3000, 5000, 8000];

// Response Interceptor - cold start 재시도 + 에러 처리 + 토큰 갱신
apiClient.interceptors.response.use(
  (response) => {
    // Cold start 재시도 후 성공 시 알림
    if (response.config && (response.config as any)._coldStartRetryCount > 0) {
      notifyColdStartResolved();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // === 1. Cold start 재시도 (Fly.io auto-stop 대비) ===
    if (isColdStartError(error) && originalRequest) {
      const retryCount = (originalRequest._coldStartRetryCount || 0) + 1;
      originalRequest._coldStartRetryCount = retryCount;

      if (retryCount <= MAX_COLD_START_RETRIES) {
        notifyColdStartRetry(retryCount, MAX_COLD_START_RETRIES);
        const delay = COLD_START_DELAYS[retryCount - 1] || 8000;
        await new Promise((resolve) => setTimeout(resolve, delay));
        // 재시도 시 타임아웃 증가 (서버 시작 대기)
        originalRequest.timeout = 30000;
        return apiClient(originalRequest);
      }

      // 모든 재시도 실패
      notifyColdStartFailed();
      return Promise.reject(error);
    }

    // === 2. 기존 에러 처리 및 토큰 갱신 ===
    const errorMessage = error.response?.data?.error?.message || error.message || '';
    const statusCode = error.response?.status;

    // Refresh Token 만료 에러 감지 (500 에러 + "Refresh token expired" 메시지)
    if (statusCode === 500 && errorMessage.includes('Refresh token expired')) {
      console.error('🔐 Refresh token expired, clearing auth data...');
      await clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // 로그인, 회원가입, 토큰 갱신 등의 요청은 401 에러 시 리다이렉트 없이 에러를 바로 던지도록 예외 처리
    const isAuthRequest = originalRequest && originalRequest.url && (
      originalRequest.url.includes('/auth/login') ||
      originalRequest.url.includes('/auth/register') ||
      originalRequest.url.includes('/auth/refresh')
    );

    // 401 에러이고 토큰 갱신을 아직 시도하지 않았으며, 인증 자체 요청이 아닌 경우
    if (statusCode === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      // Redux Store에서 refreshToken 읽기
      let refreshToken = null;
      try {
        const state = store.getState();
        refreshToken = state.auth.tokens?.refreshToken;
      } catch (err) {
        console.error('Failed to get refresh token:', err);
      }

      // Redux Rehydration 지연 대비: store에 없으면 로컬스토리지 직접 조회
      if (!refreshToken) {
        const persistRootRaw = localStorage.getItem('persist:hellgater-root');
        if (persistRootRaw) {
          try {
            const persistRoot = JSON.parse(persistRootRaw);
            const authState = JSON.parse(persistRoot.auth);
            refreshToken = authState?.tokens?.refreshToken;
          } catch (e) {
            // 무시
          }
        }
      }

      if (refreshToken) {
        try {
          // 토큰 갱신 시도
          const response = await axios.post<ApiResponse<{ accessToken: string; expiresIn: number }>>(
            `${BASE_URL}/auth/refresh`,
            { refreshToken }
          );

          if (response.data.success && response.data.data) {
            const { accessToken } = response.data.data;

            // Redux store에 새 토큰 저장
            // 기존 사용자 정보는 유지하고 accessToken만 업데이트
            const currentState = store.getState();
            if (currentState.auth.user && currentState.auth.tokens.refreshToken) {
              store.dispatch(
                setCredentials({
                  user: currentState.auth.user,
                  accessToken,
                  refreshToken: currentState.auth.tokens.refreshToken,
                })
              );
            }

            // 원래 요청 재시도
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return apiClient(originalRequest);
          }
        } catch (refreshError: any) {
          // 토큰 갱신 실패 - 완전 클리어 후 로그아웃
          console.error('🔐 Token refresh failed, clearing auth data...');
          await clearAuthAndRedirect();
          return Promise.reject(refreshError);
        }
      } else {
        // Refresh Token이 없으면 클리어 후 로그인 페이지로
        await clearAuthAndRedirect();
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
